import { Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { useMemo } from "react";
import { EChart } from "./EChart";

const BRAND = {
  accent: "#2c4055", secondary: "#fcab10", green: "#238f62",
  danger: "#cf5d5d", muted: "#607287", grid: "#e2e8f0", panel: "#ffffff",
  palette: ["#2c4055", "#fcab10", "#238f62", "#791e94", "#4a75c9", "#cf5d5d", "#c47c00", "#576b82"],
};

export interface CardSpec {
  card_id?: string;
  label?: string;
  value?: number | string;
  unit?: string;
  delta_pct?: number | null;
  format?: string;
  // O backend marca quando o card foi somado sobre dataset truncado pelo
  // `LIMIT` — o numero e um pedaco, nao o total.
  parcial?: boolean;
  linhas?: number | null;
}

export interface ChartSpec {
  chart_id: string;
  title?: string;
  type: string;
  dataset: string;
  x?: string;
  y?: string | string[];
  category?: string;
  value?: string;
  series?: Array<{ name: string; y: string; type?: string; color?: string }>;
  stack?: boolean;
}

export interface TableSpec {
  table_id: string;
  title?: string;
  // `field` e o que o backend manda; `key` existia antes e fica aceito para
  // nao quebrar quem ainda monte a spec a mao.
  columns?: Array<{ field?: string; key?: string; label?: string;
                    align?: "left" | "right" | "center"; format?: string }>;
  // O backend manda o NOME do dataset e as linhas vem em `pageData.datasets`.
  // `rows` continua aceito para uso direto.
  dataset?: string;
  rows?: Array<Record<string, any>>;
}

type Row = Record<string, any>;

// ---------- KPI card ----------

export function KpiCard({ spec }: { spec: CardSpec }) {
  // NÚMERO PARCIAL NÃO PODE TER CARA DE TOTAL.
  //
  // O backend marca `parcial` quando o card foi somado sobre um dataset que
  // voltou no `LIMIT`. Medido em 04/11/2026: "FATURAMENTO R$ 38,2M" em
  // Clientes era a soma das 5.000 linhas que passaram, com a empresa tendo
  // faturado R$ 513,7M no mesmo recorte.
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" sx={{
        color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", fontWeight: 600,
      }}>
        {spec.label ?? "—"}
        {spec.parcial && (
          <Typography component="span" variant="caption"
            sx={{ ml: 0.75, color: "warning.dark", fontWeight: 600 }}>
            parcial{spec.linhas ? ` · ${spec.linhas.toLocaleString("pt-BR")} linhas` : ""}
          </Typography>
        )}
      </Typography>
      <Typography variant="h5" sx={{ color: "primary.dark", mt: 0.5 }}>
        {formatCardValue(spec)}
      </Typography>
      {spec.delta_pct != null && (
        <Typography variant="caption" sx={{
          color: spec.delta_pct >= 0 ? "success.main" : "error.main", fontWeight: 600,
        }}>
          {spec.delta_pct >= 0 ? "+" : ""}{spec.delta_pct.toFixed(1)}%
        </Typography>
      )}
    </Card>
  );
}

function formatCardValue(spec: CardSpec): string {
  if (spec.value == null) return "—";
  const n = typeof spec.value === "number" ? spec.value : Number(spec.value);
  if (Number.isNaN(n)) return String(spec.value);
  if (spec.unit === "BRL" || spec.format === "currency" || (spec.label || "").toLowerCase().match(/receita|faturamento|ticket|valor/)) {
    if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
    return `R$ ${n.toFixed(2)}`.replace(".", ",");
  }
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 100_000) return n.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
  return n.toLocaleString("pt-BR");
}

// ---------- Chart ----------

export function ChartWidget({ spec, dataset }: { spec: ChartSpec; dataset: Row[] | undefined }) {
  const rows = dataset ?? [];
  const option = useMemo(() => buildOption(spec, rows), [spec, rows]);
  // Quantas barras o grafico mostra, quando ele corta. Dizer "20 de 5.000" e
  // honesto; mostrar 20 calado faria o leitor achar que sao todos.
  const cortadas = spec.type === "bar" && rows.length > MAX_BARRAS ? rows.length : 0;
  const supported = ["line", "bar", "stacked_bar", "pie", "scatter", "area"].includes(spec.type);

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: "primary.dark", fontWeight: 600 }}>
        {spec.title ?? spec.chart_id}
        {cortadas > 0 && (
          <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary", fontWeight: 400 }}>
            top {MAX_BARRAS} de {cortadas.toLocaleString("pt-BR")}
          </Typography>
        )}
      </Typography>
      {!supported ? (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">Tipo de gráfico <code>{spec.type}</code> ainda não renderizado no platform-v2.</Typography>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">Sem dados para este período/filtro.</Typography>
        </Box>
      ) : (
        <EChart option={option} height={280} />
      )}
    </Card>
  );
}

/**
 * Soma os valores que caem no mesmo rótulo do eixo X.
 *
 * O conjunto costuma vir granular (uma linha por mês x loja). Plotado cru, o
 * gráfico repetia "Centro, Centro, Centro" com uma barra por linha. Quando cada
 * rótulo já é único — o caso da série mensal —, agrupar não muda nada.
 */
function agruparPorEixo(rows: Row[], xKey: string, yKey: string): Row[] {
  if (!xKey || !yKey) return rows;
  const soma = new Map<string, number>();
  const ordem: string[] = [];
  for (const r of rows) {
    const rotulo = String(r[xKey] ?? "?");
    if (!soma.has(rotulo)) ordem.push(rotulo);
    soma.set(rotulo, (soma.get(rotulo) ?? 0) + Number(r[yKey] ?? 0));
  }
  if (ordem.length === rows.length) return rows;
  return ordem.map((rotulo) => ({ [xKey]: rotulo, [yKey]: soma.get(rotulo) ?? 0 }));
}

// BARRA COM MILHARES DE CATEGORIAS NAO E GRAFICO, E BORRAO.
//
// Medido em 04/11/2026 na jotuje: "Maiores clientes" plotava as 5.000 linhas
// do dataset — 1,18 MB de JSON virando uma mancha de barras de um pixel, com
// os rotulos ilegiveis. E o titulo diz "Maiores": o recorte ja estava na
// intencao, so nao estava no codigo.
//
// So vale para barra: linha com muitos pontos continua legivel (serie
// temporal), e cortar uma serie no tempo mentiria sobre o periodo.
const MAX_BARRAS = 20;

function topN(linhas: Row[], spec: ChartSpec, yKey: string): Row[] {
  if (spec.type !== "bar" || linhas.length <= MAX_BARRAS) return linhas;
  return [...linhas]
    .sort((a, b) => Number(b[yKey] ?? 0) - Number(a[yKey] ?? 0))
    .slice(0, MAX_BARRAS);
}

function buildOption(spec: ChartSpec, rows: Row[]): any {
  const base = {
    tooltip: { trigger: "axis", backgroundColor: BRAND.panel, borderColor: BRAND.grid, textStyle: { color: "#172434" } },
    grid: { left: 60, right: 30, top: 30, bottom: 40 },
  };
  const yKey = Array.isArray(spec.y) ? spec.y[0] : spec.y;

  if (spec.type === "pie") {
    return {
      tooltip: { trigger: "item" },
      series: [{
        type: "pie", radius: ["45%", "70%"], center: ["50%", "55%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: BRAND.panel, borderWidth: 2 },
        label: { color: BRAND.muted, formatter: "{b}\n{d}%", fontSize: 11 },
        data: agruparPorEixo(rows, spec.category ?? spec.x ?? "", spec.value ?? yKey ?? "")
          .map((r, i) => ({
            name: String(r[spec.category ?? spec.x ?? ""] ?? "?"),
            value: Number(r[spec.value ?? yKey ?? ""] ?? 0),
            itemStyle: { color: BRAND.palette[i % BRAND.palette.length] },
          })),
      }],
    };
  }

  if (spec.type === "stacked_bar" && spec.series) {
    const xs = rows.map((r) => r[spec.x ?? ""]);
    return {
      ...base,
      legend: { top: 0, textStyle: { color: BRAND.muted } },
      xAxis: { type: "category", data: xs, axisLabel: { color: BRAND.muted, fontSize: 11 } },
      yAxis: { type: "value", axisLabel: { color: BRAND.muted, formatter: fmtCompact }, splitLine: { lineStyle: { color: BRAND.grid } } },
      series: spec.series.map((s, i) => ({
        name: s.name, type: "bar", stack: "total",
        data: rows.map((r) => Number(r[s.y] ?? 0)),
        itemStyle: { color: s.color ?? BRAND.palette[i % BRAND.palette.length] },
      })),
    };
  }

  const linhas = topN(agruparPorEixo(rows, spec.x ?? "", yKey ?? ""), spec, yKey ?? "");
  const xs = linhas.map((r) => r[spec.x ?? ""]);
  const ys = linhas.map((r) => Number(r[yKey ?? ""] ?? 0));
  const t = spec.type === "area" ? "line" : spec.type;
  return {
    ...base,
    xAxis: { type: "category", data: xs, axisLabel: { color: BRAND.muted, fontSize: 11 }, axisLine: { lineStyle: { color: BRAND.grid } } },
    yAxis: { type: "value", axisLabel: { color: BRAND.muted, formatter: fmtCompact }, splitLine: { lineStyle: { color: BRAND.grid } } },
    series: [{
      type: t, smooth: t === "line",
      data: ys,
      itemStyle: t === "bar" ? {
        color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: BRAND.secondary }, { offset: 1, color: "#c47c00" }] },
        borderRadius: [4, 4, 0, 0],
      } : { color: BRAND.accent },
      lineStyle: t === "line" ? { width: 2.5, color: BRAND.accent } : undefined,
      areaStyle: spec.type === "area" ? { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(44,64,85,0.28)" }, { offset: 1, color: "rgba(44,64,85,0)" }] } } : undefined,
      barMaxWidth: 24,
    }],
  };
}

function fmtCompact(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return v.toString();
}

// ---------- Table ----------

export function TableWidget({ spec, dataset }: { spec: TableSpec; dataset?: Row[] }) {
  // A TABELA NUNCA RECEBEU AS LINHAS.
  //
  // O backend manda `{dataset: "top_customers"}` e as linhas vivem em
  // `pageData.datasets[...]` — igual ao grafico. Mas o `DashboardViewer`
  // passava so a `spec`, e aqui so se lia `spec.rows`. Resultado: "Sem
  // linhas." embaixo de um KPI dizendo 3.104 clientes e R$ 38,2M. Medido em
  // 04/11/2026 na jotuje, com 5.000 linhas no dataset.
  //
  // As colunas tinham o mesmo descasamento: o backend manda `field`, aqui se
  // lia `key`. Com um dos dois errado a tabela saía vazia ou sem cabecalho.
  const rows = dataset ?? spec.rows ?? [];
  const chave = (c: { field?: string; key?: string }) => c.field ?? c.key ?? "";
  const cols: NonNullable<TableSpec["columns"]> = spec.columns
    ?? (rows[0] ? Object.keys(rows[0]).map((k) => ({ field: k, label: k })) : []);
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1, color: "primary.dark", fontWeight: 600 }}>
          {spec.title ?? spec.table_id}
        </Typography>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">Sem linhas.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {cols.map((c) => (
                  <TableCell key={chave(c)} align={c.align ?? "left"}>{c.label ?? chave(c)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, 50).map((r, i) => (
                <TableRow key={i} hover>
                  {cols.map((c) => (
                    <TableCell key={chave(c)} align={c.align ?? "left"}
                      sx={{ fontFamily: c.format === "date" || chave(c).includes("date") || chave(c).includes("month") ? "monospace" : undefined }}>
                      {formatCell(r[chave(c)], c.format)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function formatCell(v: any, format?: string): string {
  if (v == null) return "—";
  if (format === "currency" && typeof v === "number") return `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
  if (typeof v === "number") return v.toLocaleString("pt-BR");
  return String(v);
}
